import * as CL from '@design-system/component-library';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { t } from '../../common/i18n/index.js';
import { TableSortOrder, TableUrlParams } from './tableConstants.js';
import type { CLTableColumn, CLTableRow } from '../../common/types/componentLibrary';

import './Table.scss';

export interface TableProps {
  columns: CLTableColumn[];
  isLoading?: boolean;
  noItemsText?: string;
  rows: CLTableRow[];
  valignText?: 'top' | 'middle' | 'bottom';
  onSortChange?: () => void;
  tableType?: 'nude' | 'bordered' | 'striped';
  rowClicks?: (() => void)[];
  showHeaders?: boolean;
  noMarginTop?: boolean;
}

export const Table = ({
  columns,
  isLoading = false,
  noItemsText,
  rows,
  valignText = 'middle',
  onSortChange,
  tableType = 'striped',
  rowClicks,
  showHeaders = true,
  noMarginTop,
}: TableProps) => {
  const { hash, search, pathname } = useLocation();
  const navigate = useNavigate();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const sort = queryParams.get(TableUrlParams.SORT) || undefined;
  const order = queryParams.get(TableUrlParams.ORDER) || undefined;
  const searchInput = queryParams.get(TableUrlParams.SEARCH) || undefined;
  const columnsWithSort = columns.map(column => ({
    ...column,
    sorted: column.key === sort && order ? (order as CLTableColumn['sorted']) : undefined,
  })) satisfies CLTableColumn[];

  const setSort = (label: string) => {
    const column = columns.find(c => c.label === label);
    if (column) {
      if (sort && order && sort === column.key) {
        queryParams.set(TableUrlParams.ORDER, order === TableSortOrder.ASC ? TableSortOrder.DESC : TableSortOrder.ASC);
      } else {
        queryParams.set(TableUrlParams.SORT, column.key);
        queryParams.set(TableUrlParams.ORDER, column.sorted === 'asc' ? TableSortOrder.DESC : TableSortOrder.ASC);
      }
      if (onSortChange) {
        onSortChange();
      }
      navigate(`${pathname}?${queryParams}${hash}`, { replace: true });
    }
  };

  // The table-x-overflow__fix-${rows.length} is a solution for the table x overflow issue that is
  // discussed and clarified this PR https://github.devcloud.elisa.fi/onlinefirst/online/pull/10448
  // Shortly. In the table the child can only be as tall / long as the parent allows it to be.
  // This fix essentially adds padding at the bottom of the table to give the table room to expand and then
  // adds negative bottom magin to draw the positive padding back to the original place.
  return (
    <>
      <div
        className={`of-table-wrapper table-x-overflow__fix-${rows.length} ${noMarginTop ? '' : dsClass.MARGIN_TOP_4}`}
      >
        <div className="of-table-content">
          <div className="of-table">
            {isLoading ? (
              <div className={`${dsClass.DISPLAY_FLEX} ${dsClass.PADDING_8} ${dsClass.JUSTIFY_CONTENT_CENTER}`}>
                <CL.LoadingSpinner size="xl" />
              </div>
            ) : (
              <CL.Table
                columns={columnsWithSort}
                hideHeadersOnMobile={false}
                onSortClick={setSort}
                rows={rows}
                tableType={tableType}
                valignText={valignText}
                rowClicks={rowClicks}
                showHeaders={showHeaders}
              />
            )}
          </div>
        </div>
        {rows.length === 0 && !isLoading && (
          <div className="no-results">
            {searchInput ? (
              <>
                <span>{t.OQBT('No results for')} </span>
                <strong>&quot;{searchInput}&quot;</strong>.
              </>
            ) : (
              (noItemsText ?? `${noItemsText}`)
            )}
          </div>
        )}
      </div>
    </>
  );
};
